<script lang="ts">
	import { devices_status } from 'src/stores/device-runtime/status';
	import {
		ControlsContainer,
		DeviceButton,
		DeviceButtonV2,
		Text,
		type DeviceButtonV2PropsTypes,
	} from '@Shelly/shelly-design-system';
	import { format } from 'svelte-i18n';
	import { boostVentoraFan, stopVentoraFanBoost } from 'src/services/ventora_fan';
	import { readVentoraFanBoostActive } from 'src/components/DevicesSettings/Settings/SettingsG2/VentoraFan/ventora_fan_settings_helpers';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	import { real_id } from 'src/helpers/interface_helpers';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	import { capFirstChar } from 'src/helpers/utilities';
	import { hasControlRights } from 'src/helpers/user_helpers';

	export let device;
	export let status;
	export let useV2Components = false;
	export let className = '';
	export let isInSlot = false;
	export let deviceButtonV2Props: Partial<DeviceButtonV2PropsTypes> = {
		size: 'm',
	};
	export let isWide = false;
	export let size: 's' | 'm' | 'l' = 'm';
	export { className as class };

	$: statusStore = getDeviceStatus(device.id);
	$: status = $statusStore;

	$: hasRights = hasControlRights(device);
	const hasTimer = false;
	const timer = { time: undefined, percentage: undefined };

	let isLoading = false;

	const toMediumOrLarge = (value: 's' | 'm' | 'l', wide: boolean): 'm' | 'l' =>
		wide ? 'l' : value === 's' ? 'm' : value;

	$: isBoostActive = readVentoraFanBoostActive(
		status?.['fan:0'] as Record<string, unknown> | undefined,
	);

	$: compactLegacySize = toMediumOrLarge(size, isWide);

	$: tagline = isBoostActive
		? $format('ventora_boost_tagline_active', { default: 'Boost on' })
		: $format('ventora_boost_tagline_inactive', { default: 'Boost off' });

	const a11yProps = {
		timerAriaLabel: $format('button_boost', { default: 'Boost' }),
	};

	const updateLocalStatus = (nextBoostActive: boolean) => {
		if (!status?.['fan:0']) return;
		status['fan:0'].boost = {
			...status['fan:0'].boost,
			active: nextBoostActive,
		};
		if (nextBoostActive) {
			status['fan:0'].on = true;
		}
		devices_status.update_device_status({
			id: real_id(device.id),
			msg: status,
		});
	};

	const handleClick = async (e: MouseEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (isLoading || !status?.online || !hasRights) return;
		isLoading = true;

		try {
			if (isBoostActive) {
				const stopRes = await stopVentoraFanBoost(device);
				if (stopRes) {
					updateLocalStatus(false);
				}
			} else {
				const boostRes = await boostVentoraFan(device, { id: 0 });
				if (boostRes) {
					updateLocalStatus(true);
				}
			}
		} finally {
			isLoading = false;
		}
	};
</script>

{#if (isWide || isInSlot) && !useV2Components}
	<ControlsContainer class={classMap({ 'height-full': isInSlot })} color="color-0">
		<svelte:fragment slot="first">
			<DeviceButton
				id="devicebutton-ventora-boost"
				{isWide}
				{isLoading}
				{hasTimer}
				{timer}
				isActive={isBoostActive}
				size={isWide ? 'l' : 'm'}
				icon="fas fa-bolt"
				aria-label={$format('button_boost', { default: 'Boost' })}
				{a11yProps}
				variant="primary"
				on:click={(e) => {
					e.stopPropagation();
					void handleClick(e);
				}}
				disabled={!status?.online || !hasRights}
				class={className}
			/>
			<Text styleType="controls-large" class="mt-s text-bold">{capFirstChar(tagline)}</Text>
			{#if isInSlot}
				<Text styleType="tiny" class="mt-s"
					>{capFirstChar($format('process_device', { default: 'Device' }))}</Text
				>
			{/if}
		</svelte:fragment>
	</ControlsContainer>
{:else if (isWide || isInSlot) && useV2Components}
	<ControlsContainer class={classMap({ 'height-full': isInSlot })} color="color-0">
		<svelte:fragment slot="first">
			<DeviceButtonV2
				{...deviceButtonV2Props}
				id="devicebutton-ventora-boost"
				{isWide}
				{isInSlot}
				{isLoading}
				{hasTimer}
				{timer}
				isActive={isBoostActive}
				size={isWide ? 'l' : 'm'}
				icon="fas fa-bolt"
				aria-label={$format('button_boost', { default: 'Boost' })}
				{a11yProps}
				variant="primary"
				on:click={(e) => {
					e.stopPropagation();
					void handleClick(e);
				}}
				disabled={!status?.online || !hasRights}
				class={className}
			/>
			<Text styleType="controls-large" class="mt-s text-bold">{capFirstChar(tagline)}</Text>
			{#if isInSlot}
				<Text styleType="tiny" class="mt-s"
					>{capFirstChar($format('process_device', { default: 'Device' }))}</Text
				>
			{/if}
		</svelte:fragment>
	</ControlsContainer>
{:else}
	{#if useV2Components}
		<DeviceButtonV2
			{...deviceButtonV2Props}
			id="devicebutton-ventora-boost-compact"
			aria-label={$format('button_boost', { default: 'Boost' })}
			{a11yProps}
			{isWide}
			{isLoading}
			{hasTimer}
			{timer}
			isActive={isBoostActive}
			icon="fas fa-bolt"
			variant="primary"
			on:click={(e) => {
				e.stopPropagation();
				void handleClick(e);
			}}
			disabled={!status?.online || !hasRights}
			class={className}
		/>
	{:else}
		<DeviceButton
			id="devicebutton-ventora-boost-compact"
			{isWide}
			{isLoading}
			{hasTimer}
			{timer}
			isActive={isBoostActive}
			size={compactLegacySize}
			icon="fas fa-bolt"
			aria-label={$format('button_boost', { default: 'Boost' })}
			{a11yProps}
			variant="primary"
			on:click={(e) => {
				e.stopPropagation();
				void handleClick(e);
			}}
			disabled={!status?.online || !hasRights}
			class={className}
		/>
	{/if}
	{#if isInSlot}
		<Text styleType="controls-large" class="mt-s text-bold">{capFirstChar(tagline)}</Text>
		<Text styleType="tiny" class="mt-s"
			>{capFirstChar($format('process_device', { default: 'Device' }))}</Text
		>
	{/if}
{/if}
